/* eslint-disable style/quote-props */
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { CardContent, Chip, Tooltip, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import CompanyLogo from '@shared/components/CompanyLogo';
import TypedCard from '@shared/components/TypedCard';
import { UserContext } from '@/shared/contexts/UserContext';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { trackEvent } from '../../../third-party/ga';
import HandlePrivateIntelligenceRequestModal from '../../review-reader/components/HandlePrivateIntelligenceRequestModal';
import { EngagementType } from './IntelligenceRequest.types';

export enum CompletedIntelligenceRequestCardMode {
  FulfillingReview = 'fulfilling-review',
}

function getStatusLabel(status: IntelligenceRequestStatus): string {
  switch (status) {
    case IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR:
      return 'Accepted';
    case IntelligenceRequestStatus.ACTIVE:
      return 'Active';
    case IntelligenceRequestStatus.CANCELLED:
      return 'Cancelled';
    case IntelligenceRequestStatus.EXPIRED:
      return 'Expired';
    case IntelligenceRequestStatus.FULFILLED:
      return 'Fulfilled';
    case IntelligenceRequestStatus.PAUSED:
      return 'Paused';
    case IntelligenceRequestStatus.PENDING:
      return 'Pending';
    case IntelligenceRequestStatus.REJECTED:
      return 'Rejected';
    default:
      return (status as string).charAt(0).toUpperCase() + (status as string).slice(1);
  }
}

function statusModifierClass(status: IntelligenceRequestStatus): string {
  switch (status) {
    case IntelligenceRequestStatus.ACTIVE:
      return 'intelligence-request-card__chip--status-active';
    case IntelligenceRequestStatus.PENDING:
      return 'intelligence-request-card__chip--status-pending';
    case IntelligenceRequestStatus.ACCEPTED_BY_REVIEW_AUTHOR:
      return 'intelligence-request-card__chip--status-accepted';
    case IntelligenceRequestStatus.FULFILLED:
      return 'intelligence-request-card__chip--status-fulfilled';
    default:
      return '';
  }
}

interface CompletedIntelligenceRequestCardProps {
  completedRequest: IntelligenceRequest;
  alignName?: 'left' | 'center' | 'right';
  chipJustifyContent?: 'flex-start' | 'center';
  highlight?: boolean;
  minHeight?: string;
  showTypeIndicator?: boolean;
  mode?: CompletedIntelligenceRequestCardMode;
  selected?: boolean;
  onRefetch?: () => Promise<void> | void;
  manageMode?: boolean;
  showActiveChip?: boolean;
  isCurated?: boolean;
}

function CompletedIntelligenceRequestCard(
  props: CompletedIntelligenceRequestCardProps,
) {
  const {
    completedRequest,
    alignName,
    highlight,
    minHeight,
    showTypeIndicator,
    selected,
    onRefetch,
    manageMode,
    showActiveChip = false,
    isCurated = false,
  } = props;

  const navigate = useNavigate();
  const { openMessage } = useMessageService();
  const { id: paramId } = useParams();
  const { user } = useContext(UserContext);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [shouldHighlight, setShouldHighlight] = useState(false);

  const fullDescription = completedRequest.description || '';
  const isLongDescription = fullDescription.length > 300;
  const displayedDescription = descriptionExpanded
    ? fullDescription
    : isLongDescription
      ? `${fullDescription.slice(0, 300)}...`
      : fullDescription;

  const handleGoToIRConfirmation = () => {
    if (!completedRequest)
      return;

    trackEvent({
      eventName: 'intelligence_request_proceed',
      eventCategory: completedRequest.company?.name,
      eventLabel: completedRequest.engagementType,
      eventValue: completedRequest.amountUsd,
    });

    if (completedRequest.engagementType === EngagementType.Review) {
      navigate({
        pathname: `/company/${encodeURIComponent(completedRequest.company!.name!)}`,
        search: `?${createSearchParams({ openReviewModal: 'true', intelligenceRequestId: String(completedRequest.id) })}`,
      });
    }
    if (completedRequest.engagementType === EngagementType.Consultation || completedRequest.engagementType === EngagementType.Direct) {
      window.open(
        'https://calendly.com/kandir/enterprise-sales-interview?back=1&month=2025-07',
      );
    }
  };

  const hasAdditionalSettings
    = (completedRequest.dealArea ?? []).length > 0
      || completedRequest.dealOutcome;

  const mapOutcome = (outcome: string) =>
    outcome === 'Yes' ? 'Deal Won' : outcome === 'No' ? 'Deal Lost' : outcome;

  const phoneConsultationWarning
    = completedRequest.engagementType === EngagementType.Consultation || completedRequest.engagementType === EngagementType.Direct
      ? 'Note: This Intelligence Request will be fulfilled via a phone consultation. If you proceed, you will be redirected to a scheduling page.'
      : '';

  const handleIntelligenceRequestClick = () => {
    // If in manage mode or user is the creator (but not curated), navigate to manage page
    if (manageMode || (!isCurated && completedRequest.userId === user?.id)) {
      navigate(`/manage-intelligence-requests?requestId=${completedRequest.id}`);
      return;
    }

    // Private requests: only the author (recipient) can respond
    if (completedRequest?.isPrivate) {
      if (completedRequest.authorId !== user?.id)
        return;

      openMessage({
        modalType: 'action',
        header: 'Private Intelligence Request',
        customBody: (
          <HandlePrivateIntelligenceRequestModal
            intelligenceRequest={completedRequest}
            onRefetch={onRefetch}
          />
        ),
        size: 'large',
      });
      return;
    }

    trackEvent({
      eventName: 'intelligence_request_warn',
      eventCategory: completedRequest.company?.name,
      eventLabel: completedRequest.engagementType,
      eventValue: completedRequest.amountUsd,
    });
    const advancedParts: string[] = [];
    if (completedRequest.dealArea?.length) {
      advancedParts.push(
        `Deal Area(s): ${completedRequest.dealArea.join(', ')}`,
      );
    }
    if (completedRequest.dealOutcome) {
      advancedParts.push(`Deal Outcome: ${completedRequest.dealOutcome}`);
    }

    openMessage({
      header: `Have you sold to ${completedRequest.company?.name}?`,
      message: (
        <>
          <Typography component="p">
            Only fulfill this Intelligence Request if you have relevant
            experience selling to
            {' '}
            <strong>{completedRequest.company?.name}</strong>
            {' '}
            and can provide
            {' '}
            <strong>valuable insights</strong>
            .
            {phoneConsultationWarning}
          </Typography>
          {advancedParts.length > 0 && (
            <Typography component="p" className="mt-16">
              This request requires you to meet the following criteria:
              {' '}
              <strong>{advancedParts.join('; ')}</strong>
              .
            </Typography>
          )}
        </>
      ),
      type: 'info',
      confirmLabel: `I Have Sold to ${completedRequest.company?.name}`,
      cancelLabel: 'Go Back',
      onConfirm: handleGoToIRConfirmation,
    });
  };

  useEffect(() => {
    if (highlight) {
      const timer = setTimeout(() => setShouldHighlight(true), 250);
      return () => clearTimeout(timer);
    }
    else {
      setShouldHighlight(false);
    }
  }, [highlight]);

  useEffect(() => {
    setAdvancedOpen(!!hasAdditionalSettings);
  }, [hasAdditionalSettings]);

  // Automatically click if selected becomes true
  useEffect(() => {
    if (selected) {
      handleIntelligenceRequestClick();
    }
    // Only run when selected changes
  }, [selected]);

  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (
      !hasOpened
      && completedRequest?.isPrivate
      && paramId
      && String(completedRequest.id) === paramId
    ) {
      openMessage({
        modalType: 'action',
        header: 'Private Intelligence Request',
        customBody: (
          <HandlePrivateIntelligenceRequestModal
            intelligenceRequest={completedRequest}
            onRefetch={onRefetch}
          />
        ),
        size: 'large',
      });
      setHasOpened(true);
    }
  }, [completedRequest, paramId, openMessage, onRefetch, hasOpened]);

  if (!completedRequest)
    return null;

  return (
    <motion.div
      animate={
        shouldHighlight
          ? {
              boxShadow: [
                '0 0 0 0px rgba(63, 92, 255, 0)',
                '0 0 0 6px rgba(63, 92, 255, 0.28)',
                '0 0 0 12px rgba(63, 92, 255, 0)',
              ],
            }
          : { boxShadow: '0 0 0 0px rgba(63, 92, 255, 0)' }
      }
      transition={{ duration: 1.8, ease: [0.4, 0, 0.2, 1] }}
    >
      <TypedCard
        onClick={manageMode ? undefined : handleIntelligenceRequestClick}
        typeName={showTypeIndicator ? 'Intelligence Request' : undefined}
        typeColor={showTypeIndicator ? '#0b74da' : undefined}
        typeDescription="Intelligence Requests are opportunities for Kandir users to earn money by providing insights on companies they have sold to. They can be for phone consultations or deep dives."
        className={`intelligence-request-card ${selected ? 'intelligence-request-card--selected' : ''} ${isLongDescription ? 'intelligence-request-card--has-long-description' : ''} ${manageMode ? 'intelligence-request-card--manage-mode' : ''}`}
        style={{ minHeight: minHeight ?? 'auto' }}
      >
        <CardContent className="intelligence-request-card__content">
          <div className="intelligence-request-card__main-content">
            <div className="intelligence-request-card__header">
              <div className="intelligence-request-card__header-lede">
                {completedRequest.company?.id && (
                  <CompanyLogo
                    className="intelligence-request-card__company-logo"
                    companyName={completedRequest.company?.name ?? ''}
                    companyId={completedRequest.company?.id}
                    companyUrl={completedRequest.company?.url}
                    alt={completedRequest.company?.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/company/${encodeURIComponent(completedRequest.company!.name!)}`);
                    }}
                  />
                )}

                <Typography
                  variant="h6"
                  className={`intelligence-request-card__company-name ${alignName ? `intelligence-request-card__company-name--${alignName}` : ''}`}
                >
                  {completedRequest.company?.id
                    ? completedRequest.company.name
                    : 'User wants to discuss your experiences'}
                </Typography>
              </div>

              {isCurated && (
                <Chip
                  label="Suggested for You"
                  size="small"
                  variant="outlined"
                  className="intelligence-request-card__chip intelligence-request-card__chip--curated"
                />
              )}

              {(completedRequest.status !== IntelligenceRequestStatus.ACTIVE || showActiveChip) && (
                <Chip
                  label={getStatusLabel(completedRequest.status)}
                  size="small"
                  variant="outlined"
                  className={[
                    'intelligence-request-card__chip',
                    'intelligence-request-card__chip--status',
                    statusModifierClass(completedRequest.status),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              )}
            </div>

            <div
              className="intelligence-request-card__engagement-section"
            >
              <Tooltip
                title={
                  completedRequest.engagementType === 'review'
                    ? 'Fill out an online questionnaire about your experience selling to this company.'
                    : completedRequest.engagementType === 'consultation'
                      ? 'Live consultation call about your experience selling to this company.'
                      : completedRequest.engagementType === 'direct'
                        ? 'Fulfilled via direct contact with the requestor'
                        : 'Fulfilled anonymously without revealing your identity'
                }
                arrow
              >
                <div className="intelligence-request-card__info-group">
                  <span className="intelligence-request-card__info-label">
                    Engagement Type
                  </span>
                  <Chip
                    icon={
                      completedRequest.engagementType === EngagementType.Review
                        ? <ArticleIcon />
                        : completedRequest.engagementType === EngagementType.Consultation
                          ? <PhoneIcon />
                          : completedRequest.engagementType === EngagementType.Direct
                            ? <PersonIcon />
                            : <VisibilityOffIcon />
                    }
                    label={
                      completedRequest.engagementType === EngagementType.Review
                        ? 'Questionnaire'
                        : completedRequest.engagementType
                          === EngagementType.Consultation
                          ? 'Phone Consultation'
                          : completedRequest.engagementType
                            === EngagementType.Direct
                            ? 'Direct Contact'
                            : 'Anonymous Contact'
                    }
                    size="small"
                    variant="outlined"
                    className="intelligence-request-card__chip intelligence-request-card__chip--engagement"
                  />
                </div>
              </Tooltip>

              <Tooltip
                title={`This intelligence request pays $${completedRequest.amountUsd} USD for an approved ${
                  completedRequest.engagementType === 'review'
                    ? 'review'
                    : completedRequest.engagementType === 'consultation'
                      ? 'consultation'
                      : completedRequest.engagementType === 'direct'
                        ? 'direct contact'
                        : 'anonymous contact'
                }.`}
                arrow
              >
                <div className="intelligence-request-card__info-group">
                  <span className="intelligence-request-card__info-label">
                    Payment
                  </span>
                  <Chip
                    label={`$${completedRequest.amountUsd}`}
                    size="small"
                    variant="outlined"
                    className="intelligence-request-card__chip intelligence-request-card__chip--bounty"
                  />
                </div>
              </Tooltip>
            </div>

            <div className="intelligence-request-card__description-section">
              <span className="intelligence-request-card__description-label">
                Description
              </span>
              <p className="intelligence-request-card__description-text">
                {displayedDescription || 'No description provided.'}
              </p>
              {isLongDescription && (
                <span
                  className="intelligence-request-card__read-more"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDescriptionExpanded(!descriptionExpanded);
                  }}
                >
                  {descriptionExpanded ? 'Read less' : 'Read more'}
                </span>
              )}
            </div>

            {hasAdditionalSettings && (
              <div
                className="intelligence-request-card__additional-settings"
                onClick={(e) => {
                  e.stopPropagation();
                  setAdvancedOpen(!advancedOpen);
                }}
              >
                <span className="intelligence-request-card__additional-settings-title">
                  Additional Settings
                </span>
                {advancedOpen
                  ? <ExpandLessIcon className="intelligence-request-card__additional-settings-icon" />
                  : <ExpandMoreIcon className="intelligence-request-card__additional-settings-icon" />}
              </div>
            )}
            {advancedOpen && (
              <div className="intelligence-request-card__deal-section">
                {(completedRequest.dealArea ?? []).length > 0 && (
                  <>
                    <span className="intelligence-request-card__deal-label">
                      Valid Deal Areas
                    </span>
                    <div className="intelligence-request-card__deal-chips">
                      {completedRequest?.dealArea?.map(area => (
                        <Chip
                          key={area}
                          label={area}
                          size="small"
                          variant="outlined"
                          className="intelligence-request-card__chip intelligence-request-card__chip--deal-area"
                        />
                      ))}
                    </div>
                  </>
                )}
                {completedRequest.dealOutcome && (
                  <>
                    <span className="intelligence-request-card__deal-label">
                      Valid Deal Outcome
                    </span>
                    <div className="intelligence-request-card__deal-chips">
                      <Chip
                        label={mapOutcome(completedRequest.dealOutcome)}
                        size="small"
                        variant="outlined"
                        className="intelligence-request-card__chip intelligence-request-card__chip--deal-area"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </TypedCard>
    </motion.div>
  );
}

export default CompletedIntelligenceRequestCard;
